// Nomad-mon Go! — P7 battle engine. PURE deterministic game logic: no THREE,
// no DOM, no audio. Given the same decks + seed + action stream, every client
// computes the identical battle (multiplayer runs this in lockstep and hashes
// the state each round). The presentation layer replays the event list.
//
// Rules locked here:
// - Deck: 5 characters (1 active + 4 bench) + 2 support cards (spell/trap).
//   A side loses when 3 of its 5 characters are KO'd (KO_TO_WIN).
// - Round: both sides choose secretly; resolution order is by priority class
//   (swap > support > attack), then initiative (SWIFT first, then SPD,
//   then an alternating seed+round tiebreak — SC32: side A no longer owns it).
// - BASIC always available. SPECIAL needs full charge pips (chargeRounds);
//   charge +1 per round survived in the arena (swap-in delays charging by the
//   card's swapCost; SWIFT ignores swap costs). Ultimates once per battle.
// - Swap is that side's action; the incoming card can't attack the round it
//   enters (SWIFT ignores this) and takes HALF damage as it lands (SC32:
//   it used to be untargetable, which made swap a free infinite dodge).
// - Type wheel: attacker beats defender's type -> x2; defender resists
//   attacker's type -> -2. Keywords exactly as reminder text promises.
// - Trap: set face-down; springs on the next enemy ATTACK against the setter's
//   side; the enemy knows A trap exists, not which.

import {
  detectSynergies, arenaFor, ENRAGE, passiveFor, hasPassive,
  BOSS_ID, BOSS_MECHANIC,
} from './depth.js';
import { clampLevel, levelCard, scaleStat, isHolo } from './levelling.js';
import { mulberry32 } from './sigil/rng.js';
import {
  tierOf, multPct, fizzled, clampQ, heavyOf, glyphOf, ultOf, PRIO, STAGGER_PCT,
  PARRY_COUNTER_PCT, SURGE_MAX, SURGE_PARRY, SURGE_GUARD, SURGE_PER_ENERGY,
  SURGE_KO_REWARD, SURGE_HEIR, SURGE_START_PER_CHARGE, SURGE_PER_CHARGE_BOOST,
  strikeSurge, hurtSurge, aiQuality, expectedMultPct, DEFAULT_Q, traceMultPct, actionTimePct,
} from './sigil/rules.js';
import { sigilForRound, templateBytes } from './sigil/sigils.js';
import { decodeSig, scoreCanonical } from './sigil/score.js';
export { mulberry32 } from './sigil/rng.js';

export { detectSynergies, arenaFor, passiveFor, hasPassive } from './depth.js';

export const TYPE_ORDER = ['IRON', 'VOYAGE', 'HUSTLE', 'MYSTIC', 'CHAOS', 'HEART'];
const BEATS = {};        // type -> type it deals x2 to
for (let i = 0; i < 6; i++) BEATS[TYPE_ORDER[i]] = TYPE_ORDER[(i + 1) % 6];

// SC2-A ENERGY SYSTEM — protocol v2. Both clients MUST run the same version;
// the version is mixed into every state hash so a mismatch desyncs instantly.
// The type wheel, exported so the UI can EXPLAIN a matchup instead of
// re-deriving it (SC32 press-hold help). attacker beats defender -> x2 damage.
export function beats(attackerType, defenderType) { return BEATS[attackerType] === defenderType; }

// SC33 bumps to 4: synergies, arena effects, ENRAGED momentum and signature
// passives all change how a round resolves, so a v3 client MUST desync rather
// than quietly disagree. The version is mixed into every state hash.
// SC35 bumps to 5: THE LONG NIGHT changes how a long round resolves, and the
// gauntlet's camp/boss/bias constants change what a run is. A v4 client MUST
// desync on the first hash rather than quietly disagree.
// SC38 bumps to 6: THE OVERKILL DIVIDEND changes what a killing blow leaves
// behind — energy, spillover damage or charge, depending on which attack landed
// it. A v5 client would compute a different HP/energy/charge triple on the very
// next hash and MUST desync rather than quietly disagree.
// Card levels change HP, SPD and authored move damage. v7 also hashes level
// explicitly, so a client that applies different progression cannot lockstep.
// v9: attack actions carry the deterministic GLYPH SKILL result. Because that
// tier changes damage, an older client must fail the lockstep hash immediately.
// v10 COMBAT V3 (~/dev/nomadmon-worklog/v3/COMBAT_V3.md): the attack is a
// SIGIL. basic/heavy/special/super are SLASH/RUNE/GLYPH/ULT; the action's
// `glyph` is a trace QUALITY 0..100 (or a canonical stroke `sig` the engine
// scores itself); ENERGY and CHARGE no longer gate anything — one SURGE meter
// per side does. Every resolved number changes, so v9 clients MUST desync.
// v10 also carries COLLECTION/DECK V2 (~/dev/nomadmon-worklog/v3/COLLECTION_DECK_V2.md):
// deck.mastery (CREW MASTERED starting surge), SPECTRUM (first weakness hit
// lands neutral), and chargeFaster re-pointed at starting surge. ONE version.
// v10 also carries HOLO / AWAKEN: a Holo card whose ULT lands at trace quality
// >= AWAKEN_MIN_Q Awakens for the rest of its battle (+AWAKEN_DMG_PCT damage,
// its hits ignore GUARD). Holo = born (legendary+) or deck.holo / level >= 5.
// v11 FORGED CARDS (DUOS + FUSIONS): new fighters whose definitions come from
// data/fusions.json (both clients inject the same baked defs into cardsById).
// A fusion's signature Ult rides `specialFx` from its definition, and the
// state hash now covers every fighter's derived stat line (max HP, both move
// damages, speed), so two clients holding different definitions desync on the
// FIRST hash. ONE version.
//
// v11 SIGIL 2 (docs/COMBAT_V3.md §13): the sigil is picked per (seed, round,
// side, tier) from a 35-shape ladder; a steeper quality curve; and TIME — the
// attack action carries `ms` (guide shown -> finger up) and the damage is
// multiplied by traceMultPct(quality curve x time percent).
export const ENGINE_VERSION = 11;
export const AWAKEN_MIN_Q = 90;
export const AWAKEN_DMG_PCT = 15;
export const ENERGY_START = 2;
// `let`, not `const`, for exactly one reason: setEconomyForSim below. ES module
// bindings are live, so a sim can price a rule change against the REAL engine
// instead of a copy of it. Nothing in the app calls the setter, and there is no
// code path from user input to it — see the comment there.
export let ENERGY_GAIN = 2, ENERGY_CAP = 10;
// v10: nothing costs energy any more. Kept (all zero) because the UI and the
// sims still read it; the referee never subtracts it.
export const COST = { basic: 0, heavy: 0, special: 0, swap: 0, gastro: 0, super: 0, brace: 0, focus: 0, spell: 0, trap: 0, pass: 0 };
// SUPER ULTIMATE: charge does not stop at the ultimate threshold any more, it
// keeps climbing this much further. Reaching the top unlocks one enormous
// attack, once per battle per card.
export const SUPER_EXTRA = 3;
export const SUPER_MULT = 2.25;
export function superNeed(me) { return me.chargeNeed + SUPER_EXTRA; }

// SC38 THE OVERKILL DIVIDEND — what damage above lethal buys, per attack kind.
// See the block in doAttack for the measurement that produced it. `let` for the
// same single reason as ENERGY_GAIN: setOverkillForSim below prices a candidate
// tuning through the REAL engine instead of a copy of it.
export let OVERKILL = { spillFrac: 0.5, spillCap: 18 };
export function setOverkillForSim(o = {}) { OVERKILL = { ...OVERKILL, ...o }; return OVERKILL; }

// SIM-ONLY ECONOMY OVERRIDE — scripts/decision-quality.js --sweep and nothing
// else. Same contract as createBattle's opts.disable: it exists so a proposed
// rule change can be MEASURED through the identical resolution code before it
// is shipped, rather than reasoned about. It is process-global and it is not
// seed-pure, so it must never be called from anything the app loads; the app
// imports these values read-only (src/battle.js reads them to draw cost pips).
// A sim that calls it is a single-purpose process that exits afterwards.
export function setEconomyForSim(o = {}) {
  if (o.cap != null) ENERGY_CAP = o.cap;
  if (o.gain != null) ENERGY_GAIN = o.gain;
  for (const k of ['basic', 'heavy', 'special', 'swap', 'gastro', 'super']) {
    if (o[k] != null) COST[k] = o[k];
  }
  return { cap: ENERGY_CAP, gain: ENERGY_GAIN, ...COST };
}
// SC32: a battle is won by knocking out KO_TO_WIN of the opponent's five, not
// by clearing the whole bench. Decks are 5 characters + 2 supports.
export const DECK_CHARS = 5, DECK_SUPPORTS = 2, KO_TO_WIN = 3;
export const GASTRO_DMG = 60;
export const ATTACK_ACTIONS = Object.freeze(['basic', 'heavy', 'special', 'super', 'gastro']);
export function isAttackAction(action) { return !!action && ATTACK_ACTIONS.includes(action.type); }
// v10: an attack's `glyph` is a trace quality 0..100. Anything else is "no data".
export function normalizeQ(q) { return clampQ(q); }
// Damage ceiling for an attack TYPE on a live character — printed by the UI and
// used by the referee; one function so the two can never disagree.
export function tierCeiling(me, d, type) {
  if (type === 'basic') return me.basicDmg;
  if (type === 'heavy') return heavyOf(me.basicDmg);
  if (type === 'special') return glyphOf(me.basicDmg, me.specialDmg);
  if (type === 'super') return ultOf(me.specialDmg);
  if (type === 'gastro') return GASTRO_DMG;
  return 0;
}
// The quality an attack resolves at. Order of authority: a canonical stroke the
// engine scores itself (both clients run the same integer scorer on the same
// bytes) > the stateless AI roll > a submitted number (solo only) > default.
// In multiplayer (st.requireSig) a bare number is NOT trusted: no stroke = 0.
export function traceQuality(st, side, a) {
  const tier = tierOf(a.type);
  if (typeof a.sig === 'string') {
    const c = decodeSig(a.sig);
    const t = templateBytes(sigilForRound(st.seed, st.round, side, tier));
    return c ? scoreCanonical(c, t).q : 0;
  }
  if (a.aiGlyph === true) return aiQuality(st.seed, st.round, side, tier, a.glyphDifficulty || 'normal');
  if (st.requireSig) return 0;
  const q = clampQ(a.glyph);
  return q == null ? DEFAULT_Q : q;
}
// SC35 stall breaker — see THE LONG NIGHT in resolveRound. The median battle is
// 11.4 rounds; this starts at more than double that and escalates, so it bounds
// every battle without ever touching one that was going anywhere.
export const LONG_NIGHT = 25, LONG_NIGHT_BITE = 4;

// Every character gets a derived HEAVY move: basic damage x1.6 rounded to the
// nearest 5. Pure data derivation — identical on every client.
export function heavyMove(d, level = 1) {
  return {
    name: `Heavy ${d.moves[0].name}`,
    dmg: scaleStat(Math.round((d.moves[0].dmg * 1.6) / 5) * 5, level),
    heavy: true,
  };
}

// status timing: an effect applied during round R that should last through the
// NEXT round must outlive the end-of-round increment — `until` is compared with
// `> st.round` AFTER st.round++ has run, so "next round" = R + 2.
// (SC2-A fix: these were R + 1 before, which made every next-round status a
// silent no-op. Spells/traps now do what their reminder text promises.)
const NEXT_ROUND = 2;


// ---------------------------------------------------------------- support map
// Every support card's effect text mapped EXPLICITLY to effect primitives.
// Primitives: dmg, stunTarget, stunAttacker, atkDebuff, spdDown, stripGuard,
// pierceNext, removeEnemyTrap, removeWard, healActive, chargeBoost.
export const SUPPORT_EFFECTS = {
  // TRAPS — spring on the next enemy attack against your side
  'doodoo-doormat': {                    // "The next attacker is stunned and deals -5 damage after."
    kind: 'trap', primitives: { stunAttacker: 1, atkDebuff: 5 },
    blurb: 'Attacker is stunned next round and deals −5 damage after.',
  },
  'feet-on-the-buffet-tedx-curator-edition': {   // "caterers flee, opponent loses guard for 1 turn"
    kind: 'trap', primitives: { dmg: 10, stripGuard: 1 },
    blurb: 'Attacker takes 10 and loses GUARD/WARD for a round.',
  },
  'toilet-standoff-at-admissions': {     // "opponent's speed drops for 1 turn (disgust penalty)"
    kind: 'trap', primitives: { dmg: 8, spdDown: 1 },
    blurb: 'Attacker takes 8 and moves last next round.',
  },
  // SPELLS — one-shot, resolve immediately
  'the-913-word-hostage-memo': {         // "opponent skips next turn reading the report"
    kind: 'spell', primitives: { stunTarget: 1 },
    blurb: 'Enemy active card skips its next action.',
  },
  '16-2-vs-15-1-the-mechanical-honesty-war': {  // "...dealing bonus damage if it contradicts..."
    kind: 'spell', primitives: { dmg: 22 },
    blurb: '22 direct damage to the enemy active card.',
  },
  'unused-gopro-mounts': {               // "Reveals hidden proof, ignoring one layer of defense"
    kind: 'spell', primitives: { pierceNext: true, dmg: 10, stripGuard: 1 },
    blurb: '10 damage; strips GUARD/WARD; your next attack gains PIERCE.',
  },
  'uss-lincoln-first-report-to-relief': { // "ignores WARD keyword, reveals hidden info"
    kind: 'spell', primitives: { removeEnemyTrap: true, removeWard: true, dmg: 8 },
    blurb: '8 damage; destroys the enemy trap and strips WARD.',
  },
  // P7 WAVE 2 supports (Phase 5)
  'code-brown': {
    kind: 'trap', primitives: { dmg: 14, spdDown: 1 },
    blurb: 'Attacker takes 14 and moves last next round. Gravity.',
  },
  'the-group-chat': {
    kind: 'spell', primitives: { chargeBoost: 2, spdDown: 1 },
    blurb: '+2 charge pips; the pile-on leaves them moving last next round.',
  },
  'street-meat': {
    kind: 'spell', primitives: { dmg: 14, healActive: 14, spdDown: 1 },
    blurb: '14 damage; your active heals 14; they move last next round.',
  },
  // SC8 WAVE 3 supports
  'flour-bomb': {                        // "Attacker takes 10 and deals −5 after — flour in the eyes."
    kind: 'trap', primitives: { dmg: 10, atkDebuff: 5 },
    blurb: 'Attacker takes 10 and deals −5 after — flour in the eyes.',
  },
  'gym-bro-advice': {                    // "8 damage; the form lecture slows them"
    kind: 'spell', primitives: { dmg: 8, spdDown: 1, atkDebuff: 5 },
    blurb: '8 damage; they move last next round and deal −5 from here.',
  },
  'rocket-snack': {                      // "Your active card heals 15."
    kind: 'spell', primitives: { healActive: 22, atkDebuff: 5 },
    blurb: 'Your active heals 22; the enemy deals −5 from here. In-flight service.',
  },
};

// character special-move effect primitives for the iconic cards (hand-tagged;
// unlisted specials are pure damage — the stat line is the contract)
// v11: a card's own `specialFx` (forged fusions) when no hand-tagged entry exists.
function specialFxOf(st, id) { return CHAR_SPECIAL_EFFECTS[id] || (st.cardsById[id] && st.cardsById[id].specialFx) || null; }
export const CHAR_SPECIAL_EFFECTS = {
  'memento-ron':        { aoeDmg: 10, wipeCharge: true },   // every enemy loses 10 HP and forgets its charged move
  'demon-gaze-sheren':  { pierce: true },
  'great-migration':    { reviveAllHalf: true },            // revives every fallen ally at half HP
  'damon-the-dome':     { blind: 1 },                       // enemies' next move deals half damage
  'labborn-james':      { stunTarget: 1 },                  // stuns every enemy for one round -> stun active
  'incognegro':         { untargetable: 2 },                // cannot be targeted for two rounds
  'doodoo-doormat':     { stunAttacker: 1 },
};

// ---------------------------------------------------------------- state
// SC33 opts: { arenaType, bossSide } — both optional and both PURE. arenaType
// defaults to side A's active card type, exactly the rule the presentation layer
// already used to pick the arena art, so no new seed input is introduced and
// multiplayer lockstep is unaffected.
export function createBattle(deckA, deckB, cardsById, seed, opts = {}) {
  // deckX: { chars: [id x5], supports: [id,id] } — side A = player/host
  //
  // opts.disable — ABLATION, used only by scripts/sc33-sim.js. Every SC33
  // mechanic can be switched off at CONSTRUCTION time (never at runtime), so an
  // ablation run exercises the identical resolution code with one term removed.
  // That is what makes a per-mechanic win-rate delta a measurement rather than a
  // guess. Nothing in the app ever passes this.
  const off = opts.disable || {};
  // A disable flag may be `true` (both sides) or 'A' / 'B' (that side only).
  // Per-side ablation is what makes an attribution honest: switching a mechanic
  // off for BOTH sides measures nothing, because the drafted opponent gets the
  // same mechanic and the two effects cancel. To price the PLAYER's synergy you
  // must compare "nobody has it" against "only the player has it".
  const offFor = (flag, side) => flag === true || flag === side;
  const zero = { dmgPct: 0, energyPerRound: 0, chargeFaster: 0, surgeStart: 0, neutralizeFirstWeakness: false };
  const rawA = detectSynergies(deckA.chars, cardsById, cleanMastery(deckA.mastery));
  const rawB = detectSynergies(deckB.chars, cardsById, cleanMastery(deckB.mastery));
  const synA = offFor(off.syn, 'A') ? { ...rawA, list: [], bonus: zero } : rawA;
  const synB = offFor(off.syn, 'B') ? { ...rawB, list: [], bonus: zero } : rawB;
  const arenaType = opts.arenaType || (cardsById[deckA.chars[0]] || {}).type || 'IRON';
  const arena = off.arena ? null : arenaFor(arenaType);
  const bossSide = opts.bossSide || null;

  const mkChar = (id, syn, sideKey, levels, holoSet) => {
    const d = cardsById[id];
    const level = clampLevel(levels && levels[id]);
    const ld = levelCard(d, level);
    const pas = offFor(off.passive, sideKey) ? null : passiveFor(d);
    // charge requirement is reduced by the LABBORN cycle and by THE VEIL arena,
    // floored at 1 so a special is never free on turn one.
    const faster = (syn.bonus.chargeFaster || 0) + ((arena && arena.chargeFaster) || 0);
    return {
      id, level, hp: ld.hp, maxHp: ld.hp, spd: ld.spd,
      // v10 HOLO status (born, levelled, or a server-validated deck.holo claim)
      // and AWAKEN (once per card per battle, then for the rest of its battle).
      holo: isHolo(d, level) || !!(holoSet && holoSet.has(id)), awakened: false,
      basicDmg: ld.moves[0].dmg, specialDmg: ld.moves[1].dmg,
      charge: pas && pas.hook === 'startCharge' ? pas.amount : 0,
      chargeNeed: Math.max(1, (d.moves[1].chargeRounds || 2) - faster),
      passive: pas ? pas.key : null,
      passiveUsed: false,      // one-shot passives: secondWind, mirrorGuard, stonewall
      enragedUntil: 0,         // SC33 momentum
      koed: false,
      enteredRound: 0,        // initial deployment: settled before round 1
      swapDelayUntil: 0,      // charge gain blocked until this round
      cantActUntil: 0,        // swap-in attack block
      untargetableUntil: 0,   // STEALTH swap-in / special
      stunnedUntil: 0,
      spdDownUntil: 0,
      blindUntil: 0,          // outgoing damage halved
      atkDebuff: 0,
      wardUsed: false,
      guardStrippedUntil: 0,
      ultUsed: false,
      braceUntil: 0,          // SC2-A: incoming damage halved through this round
      swapGuardUntil: 0,      // SC32: incoming damage halved the round this card swaps in
      soiledUntil: 0,         // SC2-A gastro aftermath: outgoing damage reduced
      soilAmt: 0,             // SC35: how much — 2 for gastro, more for CONTAGION
      gastroUsed: false,      // SC2-A: once per battle per diarrhea card
      superUsed: false,       // SUPER ULTIMATE: once per battle per card
      dmgDealt: 0, kos: 0,    // MVP stats
    };
  };
  const mkSide = (deck, syn, key) => ({
    chars: deck.chars.map(id => mkChar(id, syn, key, deck.levels || {},
      new Set(Array.isArray(deck.holo) ? deck.holo.filter(h => typeof h === 'string') : []))),
    active: 0,
    supports: deck.supports.map(id => ({ id, used: false })),
    trap: null, pierceNext: false, energy: ENERGY_START,
    // v10 SURGE (0..100, per side) and the last round this side GUARDED.
    surge: 0, lastGuard: -1,
    spectrumUsed: false,     // v10 SPECTRUM one-shot
    // SC33: the deck-composition bonus, computed ONCE here and never recomputed.
    syn: syn.bonus,
    synList: syn.list.map(s => ({ key: s.key, label: s.label, blurb: s.blurb, detail: s.detail })),
    // the boss's LAST STAND is a side-level one-shot, not a card passive
    boss: bossSide === key, bossStandUsed: false,
  });
  // v10: a START-CHARGE passive on the opening active becomes starting surge.
  const sides = { A: mkSide(deckA, synA, 'A'), B: mkSide(deckB, synB, 'B') };
  for (const k of ['A', 'B']) {
    const lead = sides[k].chars[0];
    const lp = lead && lead.passive ? passiveFor(cardsById[lead.id]) : null;
    if (lp && lp.hook === 'startCharge') sides[k].surge = Math.min(SURGE_MAX, lp.amount * SURGE_START_PER_CHARGE);
    // v10: LABBORN / THE VEIL (chargeFaster) and CREW MASTERED (surgeStart)
    // bank starting surge, since charge no longer gates anything.
    const syn = sides[k].syn || {};
    const pips = (syn.chargeFaster || 0) + ((arena && arena.chargeFaster) || 0) + (syn.surgeStart || 0);
    if (pips) sides[k].surge = Math.min(SURGE_MAX, sides[k].surge + pips * SURGE_START_PER_CHARGE);
    for (const c of sides[k].chars) c.charge = 0;
  }
  return {
    seed, rng: mulberry32(seed), round: 1, over: false, winner: null,
    // Multiplayer: a bare quality number is not trusted; only a stroke counts.
    requireSig: opts.requireSig === true,
    off,
    arena: arena ? { key: arena.key, label: arena.label, banner: arena.banner, chip: arena.chip } : null,
    arenaFx: arena || null,
    sides,
    cardsById,
    log: [],
  };
}

// deck.mastery is client-computed at lock time (same trust model as levels).
// Only the three grades count; anything else is dropped, so both clients agree.
const MASTERY_GRADES = new Set(['COMPLETE', 'GOLD', 'PRISM']);
function cleanMastery(m) {
  if (!m || typeof m !== 'object') return null;
  const out = {};
  for (const k of Object.keys(m)) if (MASTERY_GRADES.has(m[k])) out[k] = m[k];
  return out;
}

const other = (s) => (s === 'A' ? 'B' : 'A');
// v10 SURGE. Clamped 0..SURGE_MAX, always emitted so the meter animates.
function addSurge(st, side, n, reason, emit) {
  const S = st.sides[side];
  const before = S.surge;
  S.surge = Math.max(0, Math.min(SURGE_MAX, S.surge + (n | 0)));
  const got = S.surge - before;
  if (got !== 0 && emit) emit({ ev: 'surge', side, amount: got, surge: S.surge, reason, full: S.surge >= SURGE_MAX });
  return got;
}
export function activeChar(st, side) { return st.sides[side].chars[st.sides[side].active]; }
function card(st, ch) { return st.cardsById[ch.id]; }
function hasKw(st, ch, kw) { return (card(st, ch).keywords || []).includes(kw); }

// ---------------------------------------------------------------- SC33 helpers
// The passive OBJECT for a live character, or null. Read from st.cardsById so a
// clone (which carries plain-data sides) resolves identically.
function pasOf(st, ch) { return ch && ch.passive ? passiveFor(card(st, ch)) : null; }
function hasPas(st, ch, key) { return !!ch && ch.passive === key; }
// arena effect lookup — arenaFx is the full record, arena is the display subset.
function arenaFx(st) { return st.arenaFx || (st.arena ? arenaFor(st.arena.key) : null); }
// What a swap actually costs in this arena. THE CROSSING makes it free.
// Exported for the UI only (SC39): the SWAP control has to print the price the
// engine will actually charge, and THE CROSSING makes it free. No rule changed
// — this is the same function the resolver already used, now readable from
// outside. ENGINE_VERSION is untouched.
export function swapCost(st) { const a = arenaFx(st); return a && a.freeSwap ? 0 : COST.swap; }
// Ablation lookup for the runtime hooks (see createBattle's opts.disable).
function ablated(st, flag, side) {
  const f = st.off && st.off[flag];
  return f === true || f === side;
}
// A card is stunned unless UNBOWED says otherwise.
function isStunned(st, ch) {
  if (!ch || ch.stunnedUntil <= st.round) return false;
  return !hasPas(st, ch, 'unbowed');
}

export function legalActions(st, side) {
  const S = st.sides[side];
  const me = activeChar(st, side);
  const acts = [];
  if (st.over) return acts;
  const stunned = isStunned(st, me);
  const blocked = me.cantActUntil > st.round && !hasKw(st, me, 'SWIFT');
  if (!stunned && !blocked) {
    // v10: the three sigils are ALWAYS available. What gates them is the hand
    // that draws them, the order they land in, and the opponent's GUARD.
    acts.push({ type: 'basic' });     // SLASH
    acts.push({ type: 'heavy' });     // RUNE
    acts.push({ type: 'special' });   // GLYPH
    // ULT: a full SURGE meter. Spends the whole meter; repeatable per fill.
    if (S.surge >= SURGE_MAX) acts.push({ type: 'super' });
    // GASTRO ULTIMATE: diarrhea-variant cards only, once per battle per card,
    // also on a full meter. The bowels do not repeat themselves.
    if (card(st, me).variant === 'diarrhea' && !me.gastroUsed && S.surge >= SURGE_MAX) {
      acts.push({ type: 'gastro' });
    }
  }
  if (!stunned) {
    // GUARD — free, always available. FOCUS is gone: there is no energy to bank.
    acts.push({ type: 'brace' });
    S.chars.forEach((c, i) => {
      if (i !== S.active && !c.koed) acts.push({ type: 'swap', to: i });
    });
    S.supports.forEach((sp, i) => {
      if (sp.used) return;
      const fx = SUPPORT_EFFECTS[sp.id];
      if (!fx) return;
      if (fx.kind === 'spell') acts.push({ type: 'spell', slot: i });
      else if (!S.trap) acts.push({ type: 'trap', slot: i });
    });
  }
  if (acts.length === 0) acts.push({ type: 'pass' });   // stunned: forced pass
  return acts;
}

// ---------------------------------------------------------------- resolution
// resolveRound(st, actions {A, B}, chooseIncoming(side) -> benchIdx)
// returns event list; mutates st.
export function resolveRound(st, actions, chooseIncoming) {
  const ev = [];
  const emit = (e) => { ev.push(e); };
  emit({ ev: 'roundStart', n: st.round });

  // sanitize: stunned/blocked sides act 'pass'
  const act = {};
  for (const side of ['A', 'B']) {
    const legal = legalActions(st, side);
    const want = actions[side] || { type: 'basic' };
    const matched = legal.find(a =>
      a.type === want.type && a.to === want.to && a.slot === want.slot);
    // GLYPH is an input attached to an otherwise-legal attack, not part of
    // legality. Preserve submitted metadata only when the requested action
    // matched; an illegal request still falls back to a clean legal action.
    act[side] = matched
      ? { ...matched, ...(Object.hasOwn(want, 'glyph') ? { glyph: want.glyph } : {}),
          ...(typeof want.sig === 'string' ? { sig: want.sig } : {}),
          ...(Number.isInteger(want.ms) ? { ms: want.ms } : {}),
          ...(want.blind === true ? { blind: true } : {}),
          ...(want.aiGlyph === true ? { aiGlyph: true, glyphDifficulty: want.glyphDifficulty } : {}) }
      : legal.find(a => a.type === 'brace')
        || legal[0];
    if (act[side].type !== want.type) emit({ ev: 'forcedAction', side, action: act[side], wanted: want.type });
  }

  // initiative: SWIFT > SPD > player/host (A)
  const speed = (side) => {
    const me = activeChar(st, side);
    const swift = hasKw(st, me, 'SWIFT') ? 1000 : 0;
    const slowed = (me.spdDownUntil > st.round && !hasPas(st, me, 'unbowed')) ? -100 : 0;
    // SC32: the tie used to ALWAYS go to side A (the player / host). On a
    // mirror matchup that is not a tiebreak, it is a 30-point win-rate gift —
    // measured 80% for A with identical decks on both sides. Ties now alternate
    // deterministically by seed+round, so neither side owns first strike and
    // multiplayer stays perfectly reproducible.
    const tie = (((st.seed ^ st.round) & 1) === (side === 'A' ? 1 : 0)) ? 0.5 : 0;
    // SC33 VANGUARD: strikes first for the opening three rounds.
    const p = pasOf(st, me);
    const van = (p && p.hook === 'initiative' && st.round <= p.until) ? p.amount : 0;
    return swift + slowed + van + me.spd + tie;
  };
  // SC35 TRIGGER COUNTING. VANGUARD has no event of its own — it silently biases
  // initiative — so "+0.1pp" could mean either "it fires and is worth nothing" or
  // "it never changes the order". Only the second reading is a design problem, so
  // the order is computed WITH and WITHOUT the bonus and the difference is emitted.
  const vanOf = (side) => {
    const p = pasOf(st, activeChar(st, side));
    return (p && p.hook === 'initiative' && st.round <= p.until) ? p.amount : 0;
  };
  const spA = speed('A'), spB = speed('B');
  const order = spA >= spB ? ['A', 'B'] : ['B', 'A'];
  if (vanOf('A') || vanOf('B')) {
    const bare = (spA - vanOf('A')) >= (spB - vanOf('B')) ? 'A' : 'B';
    if (bare !== order[0]) emit({ ev: 'vanguardFirst', side: order[0] });
  }
  // UNBOWED has the same problem: it fires by NOT applying something. Count the
  // rounds in which it actually negated a live stun, slow or blind.
  for (const side of ['A', 'B']) {
    const me = activeChar(st, side);
    if (!me || me.koed || !hasPas(st, me, 'unbowed')) continue;
    if (me.stunnedUntil > st.round || me.spdDownUntil > st.round || me.blindUntil > st.round) {
      emit({ ev: 'unbowedNegate', side, id: me.id });
    }
  }
  // brace resolves with swaps (before any attack lands); focus with attacks
  // v10: faster sigils land first (SLASH > RUNE > GLYPH/ULT); within a class
  // the SPD order above decides. Array.prototype.sort is stable (ES2019).
  const seq = [...order].sort((a, b) => (PRIO[act[b].type] ?? 0) - (PRIO[act[a].type] ?? 0));
  // Trace qualities are fixed BEFORE anything resolves, from the action alone,
  // so a mid-round KO or swap cannot change which template a stroke is scored
  // against.
  const q = {}, tp = {};
  for (const side of ['A', 'B']) if (isAttackAction(act[side])) {
    q[side] = traceQuality(st, side, act[side]);
    tp[side] = actionTimePct(st.seed, st.round, side, act[side], q[side]);   // v11 time
  }
  const slashedBy = { A: false, B: false };   // side -> took a SLASH this round

  for (const side of seq) {
    if (st.over) break;
    const a = act[side];
    if (a.type === 'pass') { emit({ ev: 'pass', side }); continue; }
    if (a.type === 'swap') doSwap(st, side, a.to, emit, false);
    else if (a.type === 'spell') doSpell(st, side, a.slot, emit);
    else if (a.type === 'trap') doTrapSet(st, side, a.slot, emit);
    else if (a.type === 'brace') doBrace(st, side, emit);
    else {
      const tier = tierOf(a.type);
      const staggered = tier === 3 && slashedBy[side];
      emit({ ev: 'glyph', side, tier, q: q[side], kind: a.type, blind: a.blind === true,
        fizzle: fizzled(tier, q[side]), mult: traceMultPct(tier, q[side], a.blind === true, tp[side]), tp: tp[side],
        type: card(st, activeChar(st, side)).type, id: activeChar(st, side).id, round: st.round,
        ...(typeof a.sig === 'string' ? { sig: a.sig } : {}) });
      doAttack(st, side, a.type, emit, chooseIncoming, { q: q[side], blind: a.blind === true, staggered, tp: tp[side] });
      if (a.type === 'basic') slashedBy[other(side)] = true;
    }
  }

  if (!st.over) {
    // ---- SC35 THE LONG NIGHT: the engine's termination guarantee ------------
    // Found by the gauntlet referee, seed 171 leg 1, at round 401: both actives
    // on single-digit HP, both sides at capped energy and full charge, and both
    // choosing FOCUS every single round forever. It is a mutual-lethal standoff
    // — whoever swings first dies to the answer — so a searcher that models the
    // opponent as "attacks maximally" scores every attack as losing a card and
    // every stance as free. Neither side is losing, so neither side ever has to
    // commit, and the battle simply never ends.
    //
    // My first hypothesis was THE CROSSING's free swaps re-creating SC32's
    // kite-dodge, and I taxed swapping harder. It did not fix this seed, and
    // re-reading the trace showed why: nobody was swapping. That change was
    // reverted rather than kept as a plausible-looking extra.
    //
    // This is a rules gap, not an AI defect: two players who both decline to act
    // must not be able to hang the game, and a human pair can reach the same
    // standoff. So the arena runs out of patience. From THE LONG NIGHT onward
    // both actives take escalating damage every round, which bounds any battle
    // at roughly round 32 no matter how the two sides behave. It is symmetric,
    // it is a pure function of st.round, and it is far beyond the 11.4-round
    // median — a battle has to be twice as long as a long one before it bites.
    if (st.round >= LONG_NIGHT) {
      const bite = (st.round - LONG_NIGHT + 1) * LONG_NIGHT_BITE;
      for (const side of ['A', 'B']) {
        const me = activeChar(st, side);
        if (me.koed) continue;
        const a = Math.min(me.hp, bite);
        me.hp -= a;
        emit({ ev: 'longNight', side, amount: a, round: st.round });
      }
      for (const side of ['A', 'B']) {
        if (st.over) break;
        checkKO(st, side, emit, chooseIncoming);
      }
    }
  }
  if (!st.over) {
    // end of round: ARENA, REGEN, charge gain, energy income
    const afx = arenaFx(st);
    // ---- SC33 arena tick. Applied to BOTH actives, symmetrically, BEFORE the
    // heal/charge/energy pass so a chip KO ends the round honestly.
    if (afx && afx.chipDmg && !st.over) {
      for (const side of ['A', 'B']) {
        const me = activeChar(st, side);
        if (me.koed) continue;
        const a = Math.min(me.hp, afx.chipDmg);
        me.hp -= a;
        emit({ ev: 'arenaChip', side, amount: a, arena: afx.key });
      }
      // resolve any chip KO before the round closes
      for (const side of ['A', 'B']) {
        if (st.over) break;
        checkKO(st, side, emit, chooseIncoming);
      }
    }
    if (afx && afx.regen && !st.over) {
      for (const side of ['A', 'B']) {
        const me = activeChar(st, side);
        if (me.koed) continue;
        const h = Math.min(afx.regen, me.maxHp - me.hp);
        if (h > 0) { me.hp += h; emit({ ev: 'arenaRegen', side, amount: h, arena: afx.key }); }
      }
    }
  }
  if (!st.over) {
    const afx = arenaFx(st);
    for (const side of ['A', 'B']) {
      const me = activeChar(st, side);
      if (me.koed) continue;
      if (hasKw(st, me, 'REGEN')) {
        const heal = Math.min(5, me.maxHp - me.hp);
        if (heal > 0) { me.hp += heal; emit({ ev: 'regen', side, amount: heal }); }
      }
      // v10: no charge pips and no energy income. The v9 energy-denominated
      // income terms (deck synergy, arena, WAR CHEST) convert to SURGE at 5:1.
      const S = st.sides[side];
      const p = pasOf(st, me);
      const income = ((S.syn && S.syn.energyPerRound) || 0)
        + ((afx && afx.energyPerRound) || 0)
        + ((p && p.hook === 'energyPerRound') ? p.amount : 0);
      if (income > 0) addSurge(st, side, income * SURGE_PER_ENERGY, 'income', emit);
    }
    // TITHE drains the OTHER side's surge, after both have been paid.
    for (const side of ['A', 'B']) {
      const me = activeChar(st, side);
      if (me.koed) continue;
      const p = pasOf(st, me);
      if (!p || p.hook !== 'drainEnergy') continue;
      const T = st.sides[other(side)];
      const lost = Math.min(T.surge, p.amount * SURGE_PER_ENERGY);
      if (lost > 0) {
        T.surge -= lost;
        emit({ ev: 'drain', side, target: other(side), amount: lost, surge: T.surge });
      }
    }
    st.round++;
  }
  emit({ ev: 'roundEnd', n: st.round - 1, over: st.over, winner: st.winner });
  return ev;
}

function doSwap(st, side, to, emit, forced) {
  const S = st.sides[side];
  const from = S.active;
  const inc = S.chars[to];
  S.active = to;
  inc.enteredRound = st.round;
  const swift = hasKw(st, inc, 'SWIFT');
  const d = st.cardsById[inc.id];
  inc.cantActUntil = swift ? 0 : st.round + 1;
  inc.swapDelayUntil = swift ? 0 : st.round + (d.swapCost || 1);
  // SC32: entering the arena no longer makes you untouchable. Blanket
  // untargetability turned swap into a free, repeatable, guaranteed full dodge
  // (measured: 1.25 attacks per battle erased, and battles that never ended).
  // The incoming card now guards as it lands instead: incoming damage halved
  // for the round it enters. Swapping is still the right answer to a bad
  // matchup — it is no longer the right answer to everything.
  inc.swapGuardUntil = st.round + 1;
  // SC33 MOMENTUM — a card that walks in over a fallen ally is ENRAGED for one
  // round: +2 energy now, +20% damage while it lasts. This is the anti-snowball
  // term. It applies to BOTH sides and ONLY on a forced entry (a voluntary swap
  // must not be able to farm it), so it can never fire twice for one KO.
  let enraged = false;
  if (forced && !ablated(st, 'enrage', side)) {
    // NEXT_ROUND semantics: the KO lands mid-round and the incoming card usually
    // cannot act until the round after, so an enrage that expired at this
    // round's end would never once be spent. It runs through the NEXT round.
    inc.enragedUntil = st.round + NEXT_ROUND + (ENRAGE.rounds - 1);
    const g = addSurge(st, side, ENRAGE.energy * SURGE_PER_ENERGY, 'enrage', emit);
    enraged = true;
    emit({ ev: 'enraged', side, idx: to, id: inc.id, surge: S.surge, gained: g, dmgPct: ENRAGE.dmgPct });
  }
  emit({ ev: forced ? 'forcedIn' : 'swap', side, from, to, id: inc.id, swift, enraged });
}

// SC2-A universal stances
// v10 GUARD (type 'brace'): halves SLASH/ULT/supports this round, PARRIES a
// GLYPH, is SHATTERED by a RUNE (see doAttack). Builds a little surge — but not
// on two guards in a row, so turtling cannot farm the ULT.
function doBrace(st, side, emit) {
  const S = st.sides[side];
  const me = activeChar(st, side);
  me.braceUntil = st.round + 1;                 // this round's incoming halved
  const fresh = S.lastGuard !== st.round - 1;
  S.lastGuard = st.round;
  const gained = fresh ? addSurge(st, side, SURGE_GUARD, 'guard', emit) : 0;
  emit({ ev: 'brace', side, surge: S.surge, gained, tired: !fresh });
}

function applyPrimitives(st, side, prim, emit, label) {
  // side = the side PLAYING the effect; target = enemy active
  const T = st.sides[other(side)];
  const me = activeChar(st, side);
  const tgt = activeChar(st, other(side));
  const out = { label };
  if (prim.dmg) {
    const dealt = dealRawDamage(st, other(side), prim.dmg, emit, { source: side });
    out.dmg = dealt;
    me.dmgDealt += dealt;
  }
  if (prim.stunTarget) { tgt.stunnedUntil = st.round + NEXT_ROUND + (prim.stunTarget - 1); out.stun = true; }
  if (prim.spdDown) { tgt.spdDownUntil = st.round + NEXT_ROUND; out.spdDown = true; }
  // SC38: atkDebuff was a TRAP-ONLY primitive. Spells can carry it now, because
  // the measurement below says a support that costs a whole turn has to DENY
  // the opponent something, and -5 on every future swing is denial a pure heal
  // or a 15-point poke is not.
  if (prim.atkDebuff) { tgt.atkDebuff += prim.atkDebuff; out.atkDebuff = prim.atkDebuff; }
  if (prim.stripGuard) { tgt.guardStrippedUntil = st.round + NEXT_ROUND; out.stripGuard = true; }
  if (prim.pierceNext) { st.sides[side].pierceNext = true; out.pierceNext = true; }
  if (prim.removeEnemyTrap && T.trap) { emit({ ev: 'trapDestroyed', side: other(side), id: T.trap.id }); T.trap = null; out.trapGone = true; }
  if (prim.removeWard) { tgt.wardUsed = true; out.wardGone = true; }
  if (prim.healActive) {
    const heal = Math.min(prim.healActive, me.maxHp - me.hp);
    me.hp += heal; out.heal = heal;
  }
  if (prim.chargeBoost) {
    out.chargeBoost = addSurge(st, side, prim.chargeBoost * SURGE_PER_CHARGE_BOOST, 'spell', emit);
  }
  return out;
}

function doSpell(st, side, slot, emit) {
  const S = st.sides[side];
  const sp = S.supports[slot];
  sp.used = true;
  const fx = SUPPORT_EFFECTS[sp.id];
  const result = applyPrimitives(st, side, fx.primitives, emit, sp.id);
  emit({ ev: 'spell', side, id: sp.id, result });
  checkKO(st, other(side), emit);
}

function doTrapSet(st, side, slot, emit) {
  const S = st.sides[side];
  const sp = S.supports[slot];
  sp.used = true;
  S.trap = { id: sp.id };
  emit({ ev: 'trapSet', side });                 // id hidden from the enemy UI
}

// raw (non-attack) damage: no type wheel, no GUARD, no THORNS. BRACE still
// halves it — a stance guards against everything.
function dealRawDamage(st, side, amount, emit, meta = {}) {
  const ch = activeChar(st, side);
  if (ch.koed) return 0;
  let amt = amount;
  if (ch.braceUntil > st.round || ch.swapGuardUntil > st.round) amt = Math.ceil(amt / 2);
  const dealt = Math.min(ch.hp, amt);
  ch.hp -= dealt;
  emit({ ev: 'damage', side, amount: dealt, raw: true, braced: amt !== amount, ...meta });
  return dealt;
}

function doAttack(st, side, kind, emit, chooseIncoming, trace = {}) {
  const atkSide = st.sides[side];
  const defSide = st.sides[other(side)];
  const me = activeChar(st, side);
  const tgt = activeChar(st, other(side));
  const d = card(st, me);
  const isGastro = kind === 'gastro';
  const isSuper = kind === 'super';
  const tier = tierOf(kind);
  const tq = clampQ(trace.q) ?? DEFAULT_Q;
  const ceiling = tierCeiling(me, d, kind);
  // Names: SLASH is the printed basic, GLYPH the printed special, ULT the
  // special at full power. RUNE keeps v9's derived heavy name.
  const move = kind === 'basic' ? { ...d.moves[0], dmg: ceiling }
    : kind === 'heavy' ? { name: `Heavy ${d.moves[0].name}`, dmg: ceiling, heavy: true }
    : isGastro ? { name: 'GASTRO ULTIMATE', dmg: ceiling }
    : isSuper ? { ...d.moves[1], name: d.moves[1].name, dmg: ceiling }
    : { ...d.moves[1], dmg: ceiling };
  const isSpecial = kind === 'special';
  // `ult` on the attack event drives the ultimate cinematic: in v10 that is the
  // SURGE ULT (and gastro), never a GLYPH.
  const isUlt = isSuper;

  // Strike surge is paid for the TRACE, before the hit resolves — a perfect
  // glyph into a parry still filled your meter. That is the comeback lever.
  if (isSuper || isGastro) {
    atkSide.surge = 0;
    emit({ ev: 'surge', side, amount: -SURGE_MAX, surge: 0, reason: 'ult', full: false });
    if (isGastro) me.gastroUsed = true;
  } else {
    addSurge(st, side, strikeSurge(tier, tq), 'trace', emit);
  }

  if (isGastro) emit({ ev: 'gastro', side, id: me.id, targetId: tgt.id });
  if (isSuper) emit({ ev: 'super', side, id: me.id, targetId: tgt.id, dmg: move.dmg });

  // STEALTH / fresh swap: target untargetable this round -> attack fizzles
  // (gastro floods the whole arena — nowhere to hide)
  if (tgt.untargetableUntil > st.round && !isGastro) {
    emit({ ev: 'attack', side, kind, move: move.name, dmg: 0, dodged: true, ult: isUlt, attackerId: me.id });
    return;
  }

  // trap springs BEFORE the hit lands
  let trapFx = null;
  if (defSide.trap) {
    const t = SUPPORT_EFFECTS[defSide.trap.id];
    const trapId = defSide.trap.id;
    defSide.trap = null;
    const prim = { ...t.primitives };
    // trap primitives hit the ATTACKER (owner = defender side)
    const res = {};
    let trapDmg = prim.dmg || 0;
    if (trapDmg && me.braceUntil > st.round) trapDmg = Math.ceil(trapDmg / 2);
    if (trapDmg) { res.dmg = Math.min(me.hp, trapDmg); me.hp -= res.dmg; tgt.dmgDealt += res.dmg; }
    if (prim.stunAttacker || prim.stunTarget) { me.stunnedUntil = st.round + NEXT_ROUND; res.stun = true; }
    if (prim.atkDebuff) { me.atkDebuff += prim.atkDebuff; res.atkDebuff = prim.atkDebuff; }
    if (prim.spdDown) { me.spdDownUntil = st.round + NEXT_ROUND; res.spdDown = true; }
    if (prim.stripGuard) { me.guardStrippedUntil = st.round + NEXT_ROUND; res.stripGuard = true; }
    trapFx = { id: trapId, owner: other(side), result: res };
    emit({ ev: 'trapSprung', side: other(side), id: trapId, result: res });
    if (checkKO(st, side, emit)) return;      // trap KO'd the attacker mid-swing
  }

  // The trace is part of the attack record even when the hit lands on a WARD:
  // the renderer replays the sigil from it and the tape stays complete.
  const glyphMultPct = traceMultPct(tier, tq, trace.blind === true, trace.tp);
  const traceRec = { glyph: tq, tier, glyphMult: glyphMultPct / 100, fizzle: fizzled(tier, tq) };

  // WARD: ignore the first GLYPH that hits this card each battle
  if (isSpecial && !tgt.wardUsed && hasKw(st, tgt, 'WARD') &&
      tgt.guardStrippedUntil <= st.round) {
    tgt.wardUsed = true;
    emit({ ev: 'attack', side, kind, move: move.name, dmg: 0, warded: true, ult: isUlt, trap: trapFx, attackerId: me.id,
      ...traceRec });
    return;
  }

  // v10 PARRY: a GLYPH into a GUARD does nothing, and the guard answers with a
  // counter-cut. This is the consequence that makes "always the biggest sigil"
  // a readable, punishable habit instead of the dominant strategy.
  // AWAKENED hits ignore GUARD entirely: no parry, no halving.
  const awake = !!me.awakened;
  const guarding = tgt.braceUntil > st.round && !awake;
  if (isSpecial && guarding && !tgt.koed) {
    const counter = Math.max(1, Math.floor((tgt.basicDmg * PARRY_COUNTER_PCT) / 100));
    const cut = Math.min(me.hp, counter);
    me.hp -= cut;
    tgt.dmgDealt += cut;
    addSurge(st, other(side), SURGE_PARRY, 'parry', emit);
    emit({ ev: 'attack', side, kind, move: move.name, dmg: 0, parried: true, counter: cut, ult: false,
      trap: trapFx, attackerId: me.id, targetHp: tgt.hp, attackerHp: me.hp, ...traceRec });
    checkKO(st, side, emit, chooseIncoming);
    return;
  }

  // damage math — full stat semantics
  let dmg = move.dmg || 0;
  let superEff = false, resisted = false, braced = false;
  const mp0 = pasOf(st, me);          // attacker passive, needed before the wheel
  const atkType = d.type, defType = card(st, tgt).type;
  // gastro is typeless doom: no wheel, PIERCE built in
  let spectrum = false;
  if (!isGastro && BEATS[atkType] === defType) {
    // v10 SPECTRUM: the defender's first weakness hit of the battle lands neutral.
    if (defSide.syn && defSide.syn.neutralizeFirstWeakness && !defSide.spectrumUsed) {
      defSide.spectrumUsed = true; spectrum = true;
    } else { dmg *= 2; superEff = true; }
  }
  // SC35: UNBOWED carries PIERCE. See depth.js — its debuff immunity fired once
  // every thirty-three battles, so the card needed a clause that cannot miss.
  const pierce = isGastro || hasKw(st, me, 'PIERCE') || atkSide.pierceNext ||
    (mp0 && mp0.pierce) ||
    (isSpecial && specialFxOf(st, me.id)?.pierce);
  if (!isGastro && BEATS[defType] === atkType && !pierce) { dmg = Math.max(dmg - 2, 0); resisted = true; }
  // TRACE QUALITY's exact damage position (unchanged from v9's glyph): AFTER
  // both branches of type effectiveness, BEFORE GUARD, debuffs, BRACE/STONEWALL
  // and THORNS. Integer percent, so the product is exact.
  dmg = (dmg * glyphMultPct) / 100;
  // STAGGER: a GLYPH still being drawn when a SLASH landed loses 30%.
  if (trace.staggered) dmg = (dmg * STAGGER_PCT) / 100;
  // ---- SC33 multiplicative layer -----------------------------------------
  // Deck synergy, arena, ENRAGED momentum and the attacker's own passive all
  // stack ADDITIVELY into one multiplier, so three small bonuses cannot compound
  // into a large one. The synergy term is already capped in depth.js.
  const afxA = arenaFx(st);
  const mp = pasOf(st, me);
  let mult = 1;
  let spited = false, executed = false;
  const synPct = (atkSide.syn && atkSide.syn.dmgPct) || 0;
  if (synPct) mult += synPct / 100;
  if (afxA && afxA.typeDmgPct && atkType === afxA.key) mult += afxA.typeDmgPct / 100;
  if (me.enragedUntil > st.round) mult += ENRAGE.dmgPct / 100;
  if (mp && mp.hook === 'lowHpDmg' && me.hp <= me.maxHp * mp.at) { mult += mp.pct / 100; spited = true; }
  if (mp && mp.hook === 'finisher' && tgt.hp <= tgt.maxHp * mp.at) { mult += mp.pct / 100; executed = true; }
  // SC35 WAR CHEST rider: the energy half measured -0.6pp, so the card's teeth
  // are here instead. Additive with everything else, like every other term.
  let rider = null;
  if (mp && mp.dmgPct) { mult += mp.dmgPct / 100; rider = mp.key; }
  if (awake) mult += AWAKEN_DMG_PCT / 100;
  if (mult !== 1) dmg *= mult;

  if (kind === 'basic' && hasKw(st, tgt, 'GUARD') && !pierce && !awake &&
      tgt.guardStrippedUntil <= st.round) dmg = Math.max(dmg - 3, 0);
  // BULWARK blunts basics the way GUARD does, and stacks with it — that is the
  // point of a signature passive on a legendary.
  const tp = pasOf(st, tgt);
  let bulwarked = false;
  if (tp && tp.hook === 'basicResist' && !pierce && (tp.allAttacks || kind === 'basic')) {
    if (dmg > 0) bulwarked = true;
    dmg = Math.max(dmg - tp.amount, 0);
  }
  if (me.atkDebuff) dmg = Math.max(dmg - me.atkDebuff, 0);
  // SC35: `soilAmt` lets CONTAGION bite harder than the gastro default of 2,
  // which measured +0.9pp while firing 2.80 times a battle — landing constantly
  // and doing nothing. Defaults to 2 so gastro is unchanged.
  if (me.soiledUntil > st.round) dmg = Math.max(dmg - (me.soilAmt || 2), 0);
  if (me.blindUntil > st.round && !hasPas(st, me, 'unbowed')) dmg = Math.ceil(dmg / 2);
  // v10: a RUNE SHATTERS a guard — full damage, and the guard is gone for the
  // rest of the round. Everything else is halved by guard / swap-in landing.
  let guardBroken = false;
  if (kind === 'heavy' && guarding) {
    tgt.braceUntil = st.round; guardBroken = true;
    if (tgt.swapGuardUntil > st.round) { dmg = Math.ceil(dmg / 2); braced = true; }
  } else if ((tgt.braceUntil > st.round && !awake) || tgt.swapGuardUntil > st.round) { dmg = Math.ceil(dmg / 2); braced = true; }
  // STONEWALL — the FIRST hit this card takes all battle is halved. One-shot,
  // flagged on the card so it survives a clone and hashes.
  let stonewalled = false;
  if (tp && tp.hook === 'firstHitHalved' && !tgt.passiveUsed && dmg > 0) {
    tgt.passiveUsed = true; dmg = Math.ceil(dmg * (1 - tp.pct / 100)); stonewalled = true;
  }
  atkSide.pierceNext = false;
  dmg = Math.max(Math.round(dmg), superEff ? 2 : 1);

  const dealt = Math.min(tgt.hp, dmg);
  tgt.hp -= dealt;
  me.dmgDealt += dealt;
  // v10 AWAKEN: a Holo card's ULT that LANDS with a clean trace. Decided from
  // the lockstep trace quality alone, so both clients agree.
  let awakenedNow = false;
  if (isSuper && me.holo && !me.awakened && dealt > 0 && tq >= AWAKEN_MIN_Q) {
    me.awakened = true; awakenedNow = true;
  }
  // v10 comeback: being hit fills your meter (50% of max HP in one hit = +25).
  if (dealt > 0) addSurge(st, other(side), hurtSurge(dealt, tgt.maxHp), 'hurt', emit);
  // gastro aftermath: the soiled deal -2 damage for the next two rounds
  if (isGastro && !tgt.koed) { tgt.soiledUntil = st.round + NEXT_ROUND + 1; tgt.soilAmt = Math.max(tgt.soilAmt || 0, 2); }

  // ---- SC33 defender-side passives that fire ON being hit -----------------
  let mirrored = 0, veiled = false;
  // A "big" hit is a HEAVY or a SPECIAL. Two passives key off it; gating them on
  // SPECIAL alone made both measure exactly +0.0pp, because a special lands about
  // once per battle across BOTH sides.
  const isBig = isSpecial || kind === 'heavy';
  // MIRROR GUARD — the first big hit to land is reflected at half power. It does
  // not prevent the hit (that is WARD's job); it answers it.
  if (isBig && tp && tp.hook === 'reflectSpecial' && !tgt.passiveUsed && dealt > 0) {
    tgt.passiveUsed = true;
    mirrored = Math.min(me.hp, Math.ceil(dealt * (tp.pct / 100)));
    me.hp -= mirrored;
    tgt.dmgDealt += mirrored;
  }
  // VEIL STEP — taking a special buys a round out of sight.
  if (isBig && tp && tp.hook === 'veilStep' && !tgt.koed && dealt > 0) {
    // NEXT_ROUND, not NEXT_ROUND-1: `until` is compared with `> st.round` AFTER
    // the end-of-round increment, so round+1 expires the instant it is set. That
    // off-by-one is why VEIL STEP measured exactly +0.0pp — it never once held.
    tgt.untargetableUntil = st.round + NEXT_ROUND;
    veiled = true;
  }
  // CONTAGION — whatever this card touches attacks weaker next round.
  let contagion = false;
  if (mp && mp.hook === 'soilOnHit' && dealt > 0 && !tgt.koed) {
    tgt.soiledUntil = Math.max(tgt.soiledUntil, st.round + NEXT_ROUND);
    tgt.soilAmt = Math.max(tgt.soilAmt || 0, mp.soilAmount || 2);
    contagion = true;
  }

  // v10: the iconic special-move effects ride the SURGE ULT, and only on a
  // GOOD-or-better trace — the stakes of the big moment.
  let fxOut = null;
  if (isSuper && tq >= 55 && specialFxOf(st, me.id)) {
    const p = specialFxOf(st, me.id);
    fxOut = {};
    if (p.aoeDmg) {
      // every OTHER enemy char loses aoeDmg (active already took the move)
      defSide.chars.forEach((c, i) => {
        if (i !== defSide.active && !c.koed) {
          const a = Math.min(c.hp, p.aoeDmg);
          c.hp -= a; me.dmgDealt += a;
          if (c.hp <= 0) { c.koed = true; me.kos++; emit({ ev: 'benchKO', side: other(side), idx: i, id: c.id }); }
        }
      });
      fxOut.aoe = p.aoeDmg;
    }
    if (p.wipeCharge) { addSurge(st, other(side), -SURGE_MAX, 'wiped', emit); fxOut.wipeCharge = true; }
    if (p.stunTarget && !tgt.koed) { tgt.stunnedUntil = st.round + NEXT_ROUND; fxOut.stun = true; }
    if (p.blind) { tgt.blindUntil = st.round + NEXT_ROUND + (p.blind - 1); fxOut.blind = true; }
    if (p.untargetable) { me.untargetableUntil = st.round + p.untargetable; fxOut.stealth = p.untargetable; }
    if (p.stunAttacker) { /* trap-only primitive, no-op on attack */ }
    if (p.reviveAllHalf) {
      atkSide.chars.forEach((c, i) => {
        if (c.koed) {
          c.koed = false; c.hp = Math.ceil(c.maxHp / 2);
          c.enteredRound = st.round; c.cantActUntil = st.round + 1;
          emit({ ev: 'revive', side, idx: i, id: c.id, hp: c.hp });
        }
      });
      fxOut.revive = true;
    }
  }

  // LIFESTEAL / THORNS
  let lifesteal = 0, thorns = 0;
  if (dealt > 0 && hasKw(st, me, 'LIFESTEAL')) {
    lifesteal = Math.min(Math.floor(dealt / 2), me.maxHp - me.hp);
    me.hp += lifesteal;
  }
  // SIPHON stacks with LIFESTEAL on the rare card that has both — deliberately,
  // because that card is a legendary and should feel like one.
  // Counted SEPARATELY from `lifesteal`: the LIFESTEAL keyword writes the same
  // field, so a tally that reads `e.lifesteal` credits SIPHON for keyword heals.
  let siphoned = 0;
  if (dealt > 0 && mp && mp.hook === 'siphon') {
    const s = Math.min(Math.floor(dealt * (mp.pct / 100)), me.maxHp - me.hp);
    me.hp += s; lifesteal += s; siphoned = s;
  }
  if (dealt > 0 && hasKw(st, tgt, 'THORNS') && !tgt.koed) {
    thorns = Math.min(me.hp, 5);
    me.hp -= thorns;
    tgt.dmgDealt += thorns;
  }

  emit({
    ev: 'attack', side, kind, move: move.name, ult: isUlt, gastro: isGastro, attackerId: me.id,
    dmg: dealt, superEff, resisted, braced, pierce: !!pierce,
    ...traceRec, staggered: !!trace.staggered, guardBroken,
    enraged: me.enragedUntil > st.round, mult: +mult.toFixed(3),
    mirrored, veiled, stonewalled, spectrum, awake, awakenedNow,
    // SC35 trigger counting — see scripts/sc33-sim.js. Presentation ignores these.
    bulwarked, spited, executed, contagion, siphoned, rider,
    lifesteal, thorns, fx: fxOut, trap: trapFx,
    targetHp: tgt.hp, attackerHp: me.hp,
  });
  if (awakenedNow) emit({ ev: 'awaken', side, id: me.id, q: tq });

  // ---- SC38 THE OVERKILL DIVIDEND ----------------------------------------
  // MEASURED CAUSE (scripts/sc38-overkill.js, 200 battles, 2498 judged turns):
  // 28.9% of player turns have the referee's top two actions within 0.5 eval
  // units, and 90% of those are two ATTACKS. Broken down:
  //   42.2%  both moves WIN THE BATTLE     — legitimately no decision
  //   35.3%  OVERKILL: both kill, surplus discarded, states differ only in
  //          bookkeeping the eval does not price
  //   11.4%  the two moves deal the SAME NUMBER (heavy == special)
  //   11.1%  eval-blind
  // At an hp/damage ratio below 0.5, ONE HUNDRED PERCENT of tied turns are
  // both-lethal, and the mean wasted damage is 28.3 (cheaper move) / 40.1
  // (dearer move). SC37's overkill hypothesis is confirmed: past lethal, the
  // engine could not tell the three attack buttons apart.
  //
  // THE FIX IS DIFFERENTIATION, NOT MAGNITUDE. Making ALL surplus carry over as
  // raw damage would make the biggest hit strictly best, which converts a
  // TOSS-UP into a DOMINANT turn — the naive damage-maximiser would find it and
  // the decision would still not be a decision. So the dividend belongs to ONE
  // attack, and the other two keep their existing identities (basic = cheap,
  // special = charge-gated). The board decides whether it is worth having:
  // SPILLOVER pays nothing when the KO ends the battle, nothing when the heir
  // is about to fall anyway, and most when a healthy card steps in.
  //
  // THE TWO THIRDS OF MY OWN DESIGN THAT THE SWEEP KILLED, recorded rather than
  // quietly dropped. The first draft gave each attack a different currency:
  // BASIC refunded energy ("clean kill") and SPECIAL bought charge pips back
  // ("surge"). scripts/sc37-sweep.js --overkill priced all three separately
  // over three disjoint 220-battle blocks:
  //   OFF          29.6 / 30.2 / 32.5 % interesting
  //   spill only   34.8 / 34.5 / 36.4      +4.5pp, replicates in all three
  //   energy only  29.1 / 30.3 / 32.5      +0.0pp — a no-op
  //   surge only   29.5 /  --  / 32.8      +0.0pp — a no-op
  // Energy is worth 2 eval a point and 55.8% of turns are already AT THE CAP,
  // so the refund is usually literally nothing; charge is worth 5 a pip and the
  // special had already spent it. Neither difference reaches the calibrated
  // consequence threshold of 10. Two dead knobs are not shipped: `paranoia`
  // taught this project what a knob that cannot turn costs.
  const surplus = Math.max(0, dmg - dealt);
  const koHappened = checkKO(st, other(side), emit, chooseIncoming);
  if (koHappened && !st.over && surplus > 0 && kind === 'heavy') {
    const heir = activeChar(st, other(side));
    const spill = Math.min(Math.floor(surplus * OVERKILL.spillFrac), OVERKILL.spillCap);
    if (heir && !heir.koed && spill > 0) {
      // Spillover never itself knocks out: it is pressure, not a second kill,
      // and a chain KO would hand the round a snowball the comeback rate cannot
      // afford. 0.50/18 is deliberately NOT the sweep's best row (0.60/24 read
      // ~1pp higher on interesting) — the bigger value also shortened battles
      // and a shorter battle is a smaller comeback window.
      const s = Math.min(heir.hp - 1, spill);
      if (s > 0) {
        heir.hp -= s; me.dmgDealt += s;
        emit({ ev: 'spillover', side, id: me.id, targetId: heir.id, dmg: s, hp: heir.hp });
      }
    }
  }
  checkKO(st, side, emit, chooseIncoming);     // thorns / trap self-KO
}

// How many KOs finish this side: 3 of five in a standard SC32 deck, but never
// more than the deck actually holds (so 1v1 tutorial decks still terminate).
function koTarget(S) { return Math.min(KO_TO_WIN, S.chars.length); }

function checkKO(st, side, emit, chooseIncoming) {
  const S = st.sides[side];
  const ch = activeChar(st, side);
  if (!ch.koed && ch.hp <= 0) {
    // ---- SC33: two things can refuse a knockout, in this order -------------
    // SECOND WIND (card passive, once per battle): back up at 1 HP.
    if (hasPas(st, ch, 'secondWind') && !ch.passiveUsed) {
      const sw = pasOf(st, ch);
      ch.passiveUsed = true;
      // SC35: the revive HP is the real dial (SC33's bench-heal dial was void —
      // benched cards are never damaged, so it was tuning a no-op).
      ch.hp = Math.max(1, Math.round(ch.maxHp * ((sw && sw.reviveFrac) || 0)));
      // The nerf that brought this from +20.0pp into budget: it stays up, but
      // reeling — no charge, and it loses the next round.
      if (sw && sw.wipeCharge) addSurge(st, side, -Math.ceil(S.surge / 2), 'reeling', emit);
      if (sw && sw.stun) ch.stunnedUntil = st.round + NEXT_ROUND;
      if (sw && sw.blind) ch.blindUntil = st.round + NEXT_ROUND + ((sw.blind | 0) - 1);
      if (sw && sw.slow) ch.spdDownUntil = st.round + NEXT_ROUND;
      if (sw && sw.healBench) {
        S.chars.forEach((c, i) => {
          if (i === S.active || c.koed) return;
          c.hp = Math.min(c.maxHp, c.hp + sw.healBench);
        });
      }
      emit({ ev: 'secondWind', side, idx: S.active, id: ch.id, hp: ch.hp });
      return false;
    }
    // LAST STAND (the boss's ONE unique mechanic, once per gauntlet boss):
    // the FIRST of its five to fall gets back up at half HP, fully charged.
    if (S.boss && !S.bossStandUsed) {
      S.bossStandUsed = true;
      ch.hp = Math.ceil(ch.maxHp / 2);
      if (S.surge < SURGE_MAX / 2) addSurge(st, side, SURGE_MAX / 2 - S.surge, 'lastStand', emit);
      emit({ ev: 'lastStand', side, idx: S.active, id: ch.id, hp: ch.hp, name: BOSS_MECHANIC.name });
      return false;
    }
    ch.koed = true;
    ch.hp = 0;
    const killer = activeChar(st, other(side));
    if (killer && !killer.koed) {
      killer.kos++;
      // MOMENTUM — scoring a knockout pays the killer.
      const kp = pasOf(st, killer);
      if (kp && kp.hook === 'onKoReward') {
        const K = st.sides[other(side)];
        // SC35: HP is the currency a card that just traded blows is short of.
        if (kp.heal) killer.hp = Math.min(killer.maxHp, killer.hp + kp.heal);
        addSurge(st, other(side), SURGE_KO_REWARD, 'momentum', emit);
        emit({ ev: 'passiveKoReward', side: other(side), id: killer.id, surge: K.surge });
      }
    }
    // FIELD MEDIC — the fallen card tends the bench on its way out.
    const fp = pasOf(st, ch);
    if (fp && fp.hook === 'onKoHealBench') {
      let healed = 0;
      S.chars.forEach((c, i) => {
        if (i === S.active || c.koed) return;
        const h = Math.min(fp.amount, c.maxHp - c.hp);
        if (h > 0) { c.hp += h; healed += h; }
      });
      if (healed) emit({ ev: 'benchHeal', side, id: ch.id, amount: fp.amount, total: healed });
    }
    const downed = S.chars.filter(c => c.koed).length;
    emit({ ev: 'ko', side, idx: S.active, id: ch.id, downed, need: koTarget(S) });
    const alive = S.chars.map((c, i) => (!c.koed ? i : -1)).filter(i => i >= 0);
    // SC32: a side loses when KO_TO_WIN of its five are down — you no longer
    // have to clear the whole bench. Decks smaller than KO_TO_WIN (legacy /
    // tutorial) still lose only when every card is down.
    if (downed >= koTarget(S) || alive.length === 0) {
      st.over = true;
      st.winner = other(side);
      emit({ ev: 'end', winner: st.winner });
      return true;
    }
    const to = chooseIncoming ? chooseIncoming(side, alive) : alive[0];
    doSwap(st, side, alive.includes(to) ? to : alive[0], emit, true);
    // SC35 LAST ORDERS — the fallen card readies whoever takes its place. This
    // has to run AFTER doSwap, because the heir is only known once it has been
    // chosen. Paid every time the carrier dies (0.80/battle) instead of only
    // when the bench happened to be damaged (0.15/battle).
    if (fp && fp.chargeHeir) {
      const heir = activeChar(st, side);
      if (heir && !heir.koed) {
        addSurge(st, side, SURGE_HEIR, 'lastOrders', emit);
        if (fp.healHeir) heir.hp = Math.min(heir.maxHp, heir.hp + fp.healHeir);
        emit({ ev: 'lastOrders', side, id: heir.id, from: ch.id, surge: S.surge, hp: heir.hp });
      }
    }
    return true;
  }
  return false;
}

// ---------------------------------------------------------------- state hash
// cheap deterministic hash for multiplayer lockstep assertion
export function stateHash(st) {
  let h = 2166136261 >>> 0;
  const mix = (n) => { h ^= n & 0xffff; h = Math.imul(h, 16777619) >>> 0; h ^= (n >> 16) & 0xffff; h = Math.imul(h, 16777619) >>> 0; };
  mix(ENGINE_VERSION);       // protocol v2: v1 clients desync immediately
  mix(st.round);
  // SC33: the ARENA is battle-level rule state — free swaps, chip damage, faster
  // charge. It is derived from the exchanged decks, so two honest clients always
  // agree; but if one were running a different depth.js ARENAS table without an
  // ENGINE_VERSION bump, the synergy terms below would still match and the
  // divergence would only surface as drifting HP several rounds later. Mixing
  // the arena key makes that a first-round desync instead.
  mix(st.arena ? (TYPE_ORDER.indexOf(st.arena.key) + 1) : 0);
  for (const side of ['A', 'B']) {
    const S = st.sides[side];
    mix(S.active); mix(S.trap ? 1 : 0); mix(S.energy);
    mix(S.surge); mix(S.lastGuard + 1);   // v10
    // SC33: the deck-composition bonus and the boss's one-shot are part of the
    // battle's identity — a client that computed different synergies must desync
    // on the FIRST hash, not silently drift for ten rounds.
    mix((S.syn && S.syn.dmgPct) || 0);
    mix((S.syn && S.syn.energyPerRound) || 0);
    mix((S.syn && S.syn.chargeFaster) || 0);
    mix((S.syn && S.syn.surgeStart) || 0);                 // v10 CREW MASTERED
    mix((S.syn && S.syn.neutralizeFirstWeakness) ? 1 : 0); // v10 SPECTRUM
    mix(S.spectrumUsed ? 1 : 0);
    mix(S.bossStandUsed ? 1 : 0);
    for (const c of S.chars) {
      mix(c.level); mix(c.hp); mix(c.charge); mix(c.koed ? 1 : 0);
      mix(c.stunnedUntil); mix(c.untargetableUntil); mix(c.atkDebuff);
      mix(c.braceUntil); mix(c.swapGuardUntil); mix(c.soiledUntil); mix(c.soilAmt || 0); mix(c.gastroUsed ? 1 : 0);
      mix(c.blindUntil); mix(c.spdDownUntil); mix(c.guardStrippedUntil);
      mix(c.enragedUntil); mix(c.passiveUsed ? 1 : 0); mix(c.chargeNeed);
      mix(c.holo ? 1 : 0); mix(c.awakened ? 1 : 0);          // v10 HOLO / AWAKEN
      mix(c.maxHp); mix(c.basicDmg); mix(c.specialDmg); mix(c.spd);   // v11 stat line
    }
    for (const sp of S.supports) mix(sp.used ? 1 : 0);
  }
  return h.toString(16);
}

// ---------------------------------------------------------------- AI
// SC32 rebuild. Three tiers that share ONE evaluator, so the difficulty ladder
// is a search-depth ladder rather than three unrelated piles of heuristics.
//
//   EASY   — attack-biased random. A genuine beginner: it fights back, it
//            occasionally stumbles into the right answer, it never plans.
//   NORMAL — 1-ply search: every legal action simulated against the opponent's
//            single predicted reply, scored by evalState, plus hard lethal
//            counting and ultimate discipline. Competent, not clairvoyant.
//   WRATH  — 3-ply minimax (my action vs their best reply vs my best follow-up)
//            over a pruned action set, with lethal counting and an anti-stall
//            tax. Deterministic: resolveRound never consumes rng, so plain-data
//            clones stay in lockstep.
//
// WHY THE OLD NORMAL WAS A PUSHOVER (measured in scripts/engine-sim.js):
//   - it was statistically indistinguishable from EASY (67.0% vs 65.5% player
//     win-rate) — the difficulty selector did nothing;
//   - it exploited an available type advantage only 57% of the time it was on
//     offer, and swapped ~0.1 times per battle;
//   - 43% of its specials were fired at targets that survived, burning the
//     once-per-battle ultimate for nothing.

// Cumulative worth of having knocked out n of the opponent's five. The third KO
// ends the battle, so the curve accelerates: the AI should want the finisher far
// more than it wants the opener, and should defend its own third slot hardest.
const KO_LADDER = [0, 70, 190, 400];
function koWorth(n) { return KO_LADDER[Math.min(n, KO_LADDER.length - 1)]; }

// Side-relative position score. Positive = good for `side`.
export function evalState(s, side) {
  if (s.over) return s.winner === side ? 1e6 : -1e6;
  const opp = other(side);
  const me = s.sides[side], them = s.sides[opp];
  let v = 0;
  let myDown = 0, theirDown = 0;
  for (const c of me.chars) {
    if (c.koed) { myDown++; continue; }
    v += c.hp;
  }
  for (const c of them.chars) {
    if (c.koed) { theirDown++; continue; }
    v -= c.hp;
  }
  v += koWorth(theirDown) - koWorth(myDown);
  // v10: a full meter is an ULT in hand — worth roughly 40 HP.
  v += ((me.surge || 0) - (them.surge || 0)) * 0.4;
  // holding the type advantage is worth real tempo
  const a = activeChar(s, side), b = activeChar(s, opp);
  if (!a.koed && !b.koed) {
    const ad = s.cardsById[a.id], bd = s.cardsById[b.id];
    if (BEATS[ad.type] === bd.type) v += 18;
    if (BEATS[bd.type] === ad.type) v -= 18;
  }
  // an armed trap is latent damage plus a real deterrent
  if (me.trap) v += 12;
  if (them.trap) v -= 12;
  // SC33 — BURNING THE BOSS'S LAST STAND IS PROGRESS.
  // Without this term the AI deadlocks completely, and the sim caught it: the
  // boss revives to HALF ITS MAXIMUM, so a killing blow against a card sitting
  // on 33 HP puts it back to 63. A searcher that scores positions by enemy HP
  // therefore reads every attack as RAISING the enemy's health and stops
  // attacking — and because it stops attacking, the revive never fires, the
  // flag never clears, and the aversion is permanent. Measured: the player
  // landed two attacks in round 1-2 and then zero for the rest of the battle,
  // losing 0-3 to a boss deck weaker than its own.
  // 60 broke the total deadlock but left the AI idling while the revive was
  // still pending; 150 prices it at more than the HP it hands back.
  const STAND_WORTH = 150;
  if (them.boss && them.bossStandUsed) v += STAND_WORTH;
  if (me.boss && me.bossStandUsed) v -= STAND_WORTH;
  // unspent supports are dead cards by the end
  v += me.supports.filter(sp => !sp.used).length * 4;
  return v;
}

function cloneForSim(st) {
  return {
    seed: st.seed, rng: () => 0.5, round: st.round, over: st.over, winner: st.winner,
    requireSig: false,
    // SC33: the arena is battle-level state. Dropping it here would make the AI
    // search a DIFFERENT game than the one being played — it would plan against
    // no chip damage, no free swaps and no fast charge, and the resulting choice
    // would still be deterministic but systematically wrong.
    arena: st.arena, arenaFx: st.arenaFx, off: st.off,
    sides: JSON.parse(JSON.stringify(st.sides)),
    cardsById: st.cardsById, log: [],
  };
}

// Simulate one round of (mine, theirs) from st. Returns the resulting state, or
// null if the pair is illegal in a way resolveRound cannot sanitize.
function simRound(st, side, mine, theirs) {
  const sim = cloneForSim(st);
  const acts = {}; acts[side] = mine; acts[other(side)] = theirs;
  try { resolveRound(sim, acts, (s2, alive) => bestIncoming(sim, s2, alive)); } catch { return null; }
  return sim;
}

// Who to send in after a KO: the bench card that best answers what is standing
// across from it (type advantage first, then bulk). Used by the AI and by the
// forced-swap callback so replacements are not just "the leftmost survivor".
export function bestIncoming(st, side, alive) {
  const opp = other(side);
  const foe = activeChar(st, opp);
  const foeD = foe ? st.cardsById[foe.id] : null;
  let best = alive[0], bestScore = -Infinity;
  for (const i of alive) {
    const c = st.sides[side].chars[i];
    const d = st.cardsById[c.id];
    let sc = c.hp * 0.5 + c.charge * 4;
    if (foeD) {
      if (BEATS[d.type] === foeD.type) sc += 40;
      if (BEATS[foeD.type] === d.type) sc -= 40;
    }
    if (sc > bestScore) { bestScore = sc; best = i; }
  }
  return best;
}

// Damage this action would actually land right now, used for lethal counting.
// Mirrors doAttack's math closely enough to be trusted for "does this kill?".
function estimateDamage(st, side, a) {
  const me = activeChar(st, side);
  const tgt = activeChar(st, other(side));
  if (!me || !tgt) return 0;
  const d = st.cardsById[me.id], td = st.cardsById[tgt.id];
  let base;
  if (isAttackAction(a)) base = tierCeiling(me, d, a.type);
  else if (a.type === 'spell') {
    const fx = SUPPORT_EFFECTS[st.sides[side].supports[a.slot].id];
    return (fx && fx.primitives.dmg) || 0;
  } else return 0;
  const gastro = a.type === 'gastro';
  const mpP = pasOf(st, me);
  const pierce = gastro || hasKw(st, me, 'PIERCE') || st.sides[side].pierceNext ||
    (mpP && mpP.pierce) ||
    (a.type === 'special' && specialFxOf(st, me.id)?.pierce);
  if (!gastro && BEATS[d.type] === td.type) base *= 2;
  if (!gastro && BEATS[td.type] === d.type && !pierce) base = Math.max(base - 2, 0);
  // v10: the EXPECTED trace multiplier (normal hand) unless a quality is known.
  const kq = clampQ(a.glyph);
  base = (base * (kq == null ? expectedMultPct(tierOf(a.type), 'normal') : multPct(tierOf(a.type), kq))) / 100;
  // SC33: mirror doAttack's multiplicative layer, or the AI's lethal counting
  // silently under-reads every kill once synergies and arenas are live.
  const afxE = arenaFx(st);
  const mpE = pasOf(st, me);
  let multE = 1;
  const synE = (st.sides[side].syn && st.sides[side].syn.dmgPct) || 0;
  if (synE) multE += synE / 100;
  if (afxE && afxE.typeDmgPct && d.type === afxE.key) multE += afxE.typeDmgPct / 100;
  if (me.enragedUntil > st.round) multE += ENRAGE.dmgPct / 100;
  if (mpE && mpE.hook === 'lowHpDmg' && me.hp <= me.maxHp * mpE.at) multE += mpE.pct / 100;
  if (mpE && mpE.hook === 'finisher' && tgt.hp <= tgt.maxHp * mpE.at) multE += mpE.pct / 100;
  if (mpE && mpE.dmgPct) multE += mpE.dmgPct / 100;      // SC35 WAR CHEST rider
  const awakeE = !!me.awakened;
  if (awakeE) multE += AWAKEN_DMG_PCT / 100;
  if (multE !== 1) base *= multE;
  if (a.type === 'basic' && hasKw(st, tgt, 'GUARD') && !pierce && !awakeE &&
      tgt.guardStrippedUntil <= st.round) base = Math.max(base - 3, 0);
  const tpE = pasOf(st, tgt);
  if (tpE && tpE.hook === 'basicResist' && !pierce && (tpE.allAttacks || a.type === 'basic')) {
    base = Math.max(base - tpE.amount, 0);
  }
  if (me.atkDebuff) base = Math.max(base - me.atkDebuff, 0);
  if (me.soiledUntil > st.round) base = Math.max(base - (me.soilAmt || 2), 0);
  if (me.blindUntil > st.round && !hasPas(st, me, 'unbowed')) base = Math.ceil(base / 2);
  // v10 guard: a GLYPH is parried (0), a RUNE shatters it (no halving).
  if (a.type === 'special' && tgt.braceUntil > st.round && !awakeE) return 0;
  if ((tgt.braceUntil > st.round && a.type !== 'heavy' && !awakeE) || tgt.swapGuardUntil > st.round) base = Math.ceil(base / 2);
  if (tpE && tpE.hook === 'firstHitHalved' && !tgt.passiveUsed) base = Math.ceil(base * (1 - tpE.pct / 100));
  // WARD eats the first special outright
  if (a.type === 'special' && !tgt.wardUsed && hasKw(st, tgt, 'WARD') &&
      tgt.guardStrippedUntil <= st.round) return 0;
  if (tgt.untargetableUntil > st.round && !gastro) return 0;
  return Math.max(Math.round(base), 1);
}

// SC38: read-only export of the damage estimator so a harness can ask "does
// this action kill?" with the SAME arithmetic the engine uses. Pure function,
// no rng, no mutation — exporting it cannot perturb a battle.
export function estimateDamageForSim(st, side, a) { return estimateDamage(st, side, a); }


// The cheapest action that kills the enemy active right now, if one exists.
export function findLethal(st, side, legal) {
  const tgt = activeChar(st, other(side));
  if (!tgt || tgt.koed) return null;
  // SC33: a card that will simply stand back up is not a lethal target. Taking
  // the "free kill" shortcut here would spend an ultimate on a SECOND WIND or on
  // the boss's LAST STAND and skip the search entirely.
  if (hasPas(st, tgt, 'secondWind') && !tgt.passiveUsed) return null;
  if (st.sides[other(side)].boss && !st.sides[other(side)].bossStandUsed) return null;
  const kills = legal
    .filter(a => ['basic', 'heavy', 'special', 'super', 'gastro', 'spell'].includes(a.type))
    .filter(a => estimateDamage(st, side, a) >= tgt.hp);
  if (!kills.length) return null;
  // SC38 — THE CHEAPEST KILL WAS ALWAYS THE SUPPORT, AND THAT WAS A BUG.
  // `COST.spell` and `COST.trap` are 0, so a lethal spell sorted ahead of every
  // attack and this shortcut took it. Measured over 600 battles: 55.8% of ALL
  // support plays in the game came from here rather than from a decision, and
  // in 99.6% of them an ordinary attack ALSO killed — a one-per-battle card
  // discarded to land a kill a 1-energy basic would have landed. Half of the
  // support subsystem's already-tiny usage was this.
  // Energy is a renewable resource and a support is not, so the sort is now
  // consumables last, then cheapest.
  const consumable = (a) => (a.type === 'spell' || a.type === 'trap' ? 1 : 0);
  // v10: nothing costs energy; prefer the SAFEST sigil that kills (a SLASH
  // lands first and cannot fizzle), and never burn a full meter on a sure kill.
  kills.sort((x, y) => (consumable(x) - consumable(y)) ||
    (tierOf(x.type) - tierOf(y.type)));
  return kills[0];
}

// A cheap model of what the opponent will do, used as NORMAL's single predicted
// reply. Greedy: kill if you can, else hit as hard as you can afford.
//
// SC37 also uses this as THE NAIVE PLAYER — the person who reads none of the
// depth the last three runs added. If this function already finds the referee's
// best move, the turn had no decision in it worth the name.
export function predictReply(st, side) {
  const legal = legalActions(st, side);
  if (!legal.length) return { type: 'pass' };
  const lethal = findLethal(st, side, legal);
  if (lethal) return lethal;
  let best = legal[0], bestDmg = -1;
  for (const a of legal) {
    const dmg = estimateDamage(st, side, a);
    if (dmg > bestDmg) { bestDmg = dmg; best = a; }
  }
  return best;
}

// Shared shaping applied to a raw simulated score, so all tiers agree on what
// counts as bad play regardless of how deep they searched.
function shape(st, side, a, v) {
  // Kiting tax: swapping and stances look free in a shallow sim because the
  // cost lands next round. Price them so the AI fights instead of dancing.
  if (a.type === 'swap') v -= 25;
  if (a.type === 'brace') v -= 10;
  // v10: an ULT spends the whole meter; firing it into a target it does not
  // finish is only right when the meter would otherwise sit idle — a mild tax.
  if (a.type === 'super') {
    const tgt = activeChar(st, other(side));
    if (tgt && estimateDamage(st, side, a) < tgt.hp) v -= 12;
  }
  // Do not dump a support for nothing.
  if (a.type === 'spell') v -= 5;
  return v;
}

export function aiChooseEasy(st, side, rng = st.rng) {
  const legal = legalActions(st, side);
  const atk = legal.filter(a => ['basic', 'heavy', 'special', 'super', 'gastro'].includes(a.type));
  if (atk.length && rng() < 0.75) return atk[Math.floor(rng() * atk.length)];
  return legal[Math.floor(rng() * legal.length)];
}

// Aggregate the opponent's possible replies into one number. paranoia=1 is pure
// minimax (assume they always find the best answer), paranoia=0 is the average
// reply. Pure minimax makes the AI cower: it sees the worst case everywhere and
// braces instead of pressing an advantage, which is why the old 2-ply WRATH was
// no stronger than the 1-ply NORMAL. A blend plays the percentages instead.
function aggregate(values, paranoia) {
  if (!values.length) return -Infinity;
  const worst = Math.min(...values);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  return paranoia * worst + (1 - paranoia) * mean;
}

// Rank a scored action list and take the best — except `mistake` of the time,
// when the second-best is taken instead. This is the difficulty knob: it models
// a player who sees the board correctly but does not always find the strongest
// line, and it keeps every tier deterministic given the seed.
function pickScored(scored, rng, mistake) {
  if (!scored.length) return null;
  scored.sort((a, b) => b.v - a.v);
  if (scored.length > 1 && mistake > 0 && rng && rng() < mistake) return scored[1].a;
  return scored[0].a;
}

// Score EVERY action in the pruned set and return them sorted best-first, as
// [{ a, v }]. This is the body of searchChoose lifted out unchanged so a
// referee can read the whole ranking instead of only its argmax.
//
// SC37 exists because of what that ranking contains: the gap between the best
// and second-best action is the only direct measure of whether a turn is a
// decision at all. Nothing here consumes rng (the mistake roll is the caller's
// job, in pickScored), so exporting it cannot perturb a battle.
// SC38 adds `replies`: how many of the opponent's answers a depth-2 search
// considers, and how many of my own follow-ups it tries against each. It was
// hard-coded at 5, which makes depth 2 cost breadth x replies x (1 + replies)
// simulated rounds — 8 x 5 x 6 = 240 against depth 1's 10, a 24x main-thread
// bill on a phone. Defaulted to 5 so every existing caller is byte-identical.
export function scoreActions(st, side, { depth, paranoia, breadth, replies = 5 }) {
  const legal = legalActions(st, side);
  if (!legal.length) return [];
  const opp = other(side);

  const rank = (s, sd, a) => {
    let r = estimateDamage(s, sd, a);
    if (a.type === 'swap') r -= 5;
    if (a.type === 'trap' || a.type === 'spell') r += 6;
    if (a.type === 'brace' || a.type === 'focus') r += 2;
    return r;
  };
  const mineSet = [...legal].sort((x, y) => rank(st, side, y) - rank(st, side, x)).slice(0, breadth);

  const scored = [];
  for (const mine of mineSet) {
    let theirSet;
    if (depth === 1) {
      theirSet = [predictReply(st, opp)];
    } else {
      const ol = legalActions(st, opp);
      theirSet = [...ol].sort((x, y) => rank(st, opp, y) - rank(st, opp, x)).slice(0, replies);
      if (!theirSet.length) theirSet = [{ type: 'pass' }];
    }
    const vals = [];
    for (const theirs of theirSet) {
      const s1 = simRound(st, side, mine, theirs);
      if (!s1) continue;
      if (s1.over || depth < 2) { vals.push(evalState(s1, side)); continue; }
      // ply 3: my best follow-up against their predicted reply
      const theirNext = predictReply(s1, opp);
      const follow = [...legalActions(s1, side)]
        .sort((x, y) => rank(s1, side, y) - rank(s1, side, x)).slice(0, replies);
      let bestFollow = -Infinity;
      for (const f of follow) {
        const s2 = simRound(s1, side, f, theirNext);
        if (!s2) continue;
        const fv = shape(s1, side, f, evalState(s2, side));
        if (fv > bestFollow) bestFollow = fv;
      }
      vals.push(bestFollow === -Infinity ? evalState(s1, side) : bestFollow);
    }
    const agg = aggregate(vals, paranoia);
    if (agg === -Infinity) continue;
    scored.push({ a: mine, v: shape(st, side, mine, agg) });
  }
  scored.sort((x, y) => y.v - x.v);
  return scored;
}

// The one search both real tiers run. depth 1 = my move vs a predicted reply.
// depth 2 = my move vs their replies vs my best follow-up.
export function searchChoose(st, side, { depth, mistake, paranoia, breadth, replies, rng }) {
  const legal = legalActions(st, side);
  if (legal.length <= 1) return legal[0] || { type: 'pass' };
  // a free kill is never worth searching past
  const lethal = findLethal(st, side, legal);
  if (lethal) return lethal;
  const scored = scoreActions(st, side, { depth, paranoia, breadth, replies });
  return pickScored(scored, rng, mistake) || legal[0];
}

// NORMAL — SC38: TWO ply, and the paranoia knob finally does something.
//
// WHAT WAS WRONG. SC37 measured NORMAL agreeing with a strong referee on 35.1%
// of its turns and blundering (regret >= 80) on one turn in five — and it was
// WORST exactly where the last three runs put the depth: holding a type
// advantage -7.2pp, special charged -4.5pp, pool at cap -5.2pp, and BEST when
// too poor to have any options at all (+12.2pp). A damage-maximiser that
// cowers, whose competence was inversely proportional to how much there was to
// decide.
//
// TWO REFUTATIONS BEHIND THIS LINE.
// 1. `paranoia` was NOT the fix, and at depth 1 it is not a parameter at all:
//    theirSet is a single predicted reply, so aggregate([v], p) === v for every
//    p. SC38 re-ran it and the rows are still byte-identical — 1.00 / 0.65 /
//    0.40 all give 36.0% agree, 21.4% blunder, 14.7% brace, 64.5% player win.
//    At depth 2 it becomes LIVE (46.6% / 51.9% / 48.6%), which is why it is
//    kept at 0.65 here rather than deleted: it turns now.
// 2. Depth 2 could NOT be bought cheaply. Narrowing the reply/follow-up fan to
//    save the main thread throws away the whole gain: b6/r3 reads 38.0% agree
//    (against depth 1's 36.0%) and b5/r2 reads 33.0% — WORSE than depth 1 —
//    while distorting behaviour badly (swaps 15.9% -> 0.6%, battles 11.6 ->
//    8.4 rounds). The agreement lives in the width of the reply fan, not in the
//    extra ply on its own. So it is the full search or none.
//
// MEASURED: agreement 36.0% -> 51.9%, blunders 21.4% -> 10.2%, bracing 14.7%
// -> 5.7% (the ladder's odd passive archetype rejoins the other two tiers).
//
// THE ANDROID GATE, AND WHY IT PASSES. scripts/sc38-aiperf.mjs, headed on a
// real Metal GPU (SwiftShader refused), navigator.webdriver spoofed, timed in
// the page on the module the app actually loads:
//        throttle    d1 b10 mean/p95      d2 b8 mean/p95
//        x1           0.10 / 0.30ms        1.21 / 2.80ms
//        x4           0.29 / 1.00ms        4.81 / 11.30ms
//        x8           0.56 / 2.10ms        9.78 / 22.70ms
// The search is one synchronous call PER ROUND, between animations, so the
// number that matters is the block length, not sustained fps. At mobile-class
// x8 the worst single block is 24.4ms — at most one dropped frame, once per
// round. And the decisive fact: SHAO'S WRATH has run this EXACT search since
// SC33 and measures 9.57 / 22.70ms in the same harness. NORMAL is not being
// asked to pay a bill the game does not already pay on the same devices.
export function aiChoose(st, side, rng = st.rng) {
  return searchChoose(st, side, {
    depth: 2, mistake: 0.22, paranoia: 0.65, breadth: 8, rng,
  });
}

// SHAO'S WRATH — three ply, wider, and it plays the percentages instead of the
// worst case. No mistake rate: Shao does not miss.
export function aiChooseWrath(st, side) {
  return searchChoose(st, side, {
    depth: 2, mistake: 0, paranoia: 0.65, breadth: 8, rng: null,
  });
}
